// src/pages/Login.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import { FiUser, FiLock, FiEye, FiEyeOff, FiX } from "react-icons/fi";
import styles from "./LoginDark.module.css";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";
import InlineSpinner from "../components/common/InlineSpinner";

// Cast react-icons for TypeScript
const GoogleIcon = FaGoogle as React.FC<React.SVGProps<SVGSVGElement>>;
const UserIcon = FiUser as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeOffIcon = FiEyeOff as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

type ProviderType = "google" | "microsoft";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const {
    signInWithProvider,
    signInWithGoogle,
    requestEmailLoginOtp,
    error,
    clearError,
  } = useAuth();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);
  const [providerLoading, setProviderLoading] = useState<ProviderType | null>(
    null
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const displayedError = localError ?? error;

  // Apply a page-scoped dark theme class to remove global margins and darken navbar
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("login-dark");
      document.body.classList.add("login-dark");
      return () => {
        document.documentElement.classList.remove("login-dark");
        document.body.classList.remove("login-dark");
      };
    }
  }, []);

  const handleSocialLogin = async (provider: ProviderType) => {
    setProviderLoading(provider);
    try {
      if (provider === "google") {
        await signInWithGoogle();
      } else {
        await signInWithProvider(provider);
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Login failed";
      setLocalError(message);
    } finally {
      setProviderLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailLoading(true);
    setLocalError(null);
    try {
      if (password && password.length >= 8) {
        // Password-based login
        await authService.login({ email, password });
        navigate("/", { replace: true });
      } else {
        // Email OTP login for existing users
        await requestEmailLoginOtp(email, { rememberMe });
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Login failed";
      setLocalError(message);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowDots} aria-hidden="true">
        <div
          className={`${styles.dot} ${styles.dotBlue}`}
          style={{ top: "8%", left: "12%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ top: "20%", right: "10%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ bottom: "18%", left: "22%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotRed}`}
          style={{ bottom: "10%", right: "18%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotTeal}`}
          style={{ top: "40%", left: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPink}`}
          style={{ top: "65%", right: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ top: "32%", left: "45%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ bottom: "28%", right: "38%" }}
        />
      </div>

      <div className={styles.inner}>
        <div className={styles.pulseContainer} aria-hidden="true">
          <div className={styles.pulseCircle}></div>
        </div>
        <h1 className={styles.title}>Login to Sendupdates</h1>

        <div className={styles.card} role="region" aria-label="Login form">
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email
              </label>
              <div className={styles.inputWrapper}>
                <UserIcon className={styles.inputIconLeft} aria-hidden="true" />
                <input
                  id="email"
                  type="email"
                  placeholder="Email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailLoading}
                  autoComplete="email"
                  aria-label="Email address"
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <div className={styles.inputWrapper}>
                <LockIcon className={styles.inputIconLeft} aria-hidden="true" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  className={styles.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={emailLoading}
                  autoComplete="current-password"
                  aria-label="Password"
                />
                <button
                  type="button"
                  className={styles.inputIconRight}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className={styles.inlineActions}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <Link
                to="/login#forgot"
                className={styles.link}
                aria-label="Forgot password"
              >
                Forgot Password
              </Link>
            </div>

            <button
              type="submit"
              className={styles.primaryButton}
              disabled={emailLoading || providerLoading !== null}
              aria-label="Log in"
            >
              Log In
              {emailLoading && <InlineSpinner />}
            </button>

            {displayedError && (
              <div
                className={styles.errorAlert}
                role="alert"
                aria-live="assertive"
              >
                <div className={styles.errorAlertTitle}>Unable to sign in</div>
                <div className={styles.errorAlertMessage}>{displayedError}</div>
                <button
                  type="button"
                  className={styles.errorAlertClose}
                  aria-label="Dismiss error"
                  onClick={() => {
                    if (localError) {
                      setLocalError(null);
                    } else {
                      clearError();
                    }
                  }}
                >
                  <CloseIcon width={16} height={16} />
                </button>
              </div>
            )}

            <div className={styles.divider} aria-hidden="true">
              <span className={styles.dividerLine}></span>
              <span className={styles.dividerText}>Or Sign in With</span>
              <span className={styles.dividerLine}></span>
            </div>

            <div className={styles.socialRow}>
              <button
                type="button"
                aria-label="Sign in with Google"
                className={`${styles.socialButton} ${styles.googleButton}`}
                onClick={() => handleSocialLogin("google")}
                disabled={providerLoading !== null || emailLoading}
              >
                <GoogleIcon className={styles.socialIcon} />
                Google
                {providerLoading === "google" && <InlineSpinner />}
              </button>
            </div>
          </form>
        </div>

        <div className={styles.footer}>
          Do not have an account? <Link to="/signup">Sign up</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
